/**
 * Owner page: the early-access requests the Coming Soon page has collected,
 * newest first, one page at a time.
 *
 * Read-only by design. The dashboard shows what a person typed so somebody
 * can reach out to them; it changes nothing. A link is rendered as text and,
 * when it is an http(s) URL, as an outbound link with `rel="noopener
 * noreferrer"`; an `@handle` is text only — the page does not guess which
 * platform it names.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { useLanguage } from '../../i18n/language.js';
import {
  ApiFailure,
  fetchEarlyAccessRequests,
  type EarlyAccessRequestRecord,
} from '../api.js';
import { FailurePanel, LoadingPanel, PageTitle } from './common.js';
import { Button, EmptyState, InlineAlert } from '../ui.js';

const COPY = {
  en: {
    eyebrow: 'Owner',
    title: 'Early-access requests',
    intro:
      'People who asked for private-beta access from the Coming Soon page. Newest first. Reach out in the language they were reading.',
    empty: 'Nobody has requested early access yet.',
    name: 'Name',
    email: 'Email',
    brand: 'Brand',
    link: 'Link',
    language: 'Language',
    time: 'Requested',
    none: '—',
    languages: { en: 'English', ar: 'Arabic' },
    loadMore: 'Load more',
    loadingMore: 'Loading…',
    moreFailed: 'The next page could not be loaded.',
    retry: 'Try again',
    refresh: 'Refresh',
    count: (shown: number) => `${String(shown)} shown`,
  },
  ar: {
    eyebrow: 'المالك',
    title: 'طلبات الوصول المبكر',
    intro:
      'الأشخاص الذين طلبوا الوصول إلى النسخة التجريبية الخاصة من صفحة «قريبًا». الأحدث أولًا. تواصل معهم باللغة التي كانوا يقرؤون بها.',
    empty: 'لم يطلب أحد الوصول المبكر بعد.',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    brand: 'العلامة التجارية',
    link: 'الرابط',
    language: 'اللغة',
    time: 'وقت الطلب',
    none: '—',
    languages: { en: 'الإنجليزية', ar: 'العربية' },
    loadMore: 'تحميل المزيد',
    loadingMore: 'جارٍ التحميل…',
    moreFailed: 'تعذّر تحميل الصفحة التالية.',
    retry: 'حاول مرة أخرى',
    refresh: 'تحديث',
    count: (shown: number) => `${String(shown)} معروض`,
  },
} as const;

type PageState =
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly failure: ApiFailure }
  | {
      readonly status: 'ready';
      readonly requests: readonly EarlyAccessRequestRecord[];
      readonly nextCursor: string | null;
      readonly more: 'idle' | 'loading' | 'failed';
    };

function formatTime(iso: string, language: 'en' | 'ar'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-SA' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function LinkCell({
  link,
  none,
}: {
  readonly link: string | null;
  readonly none: string;
}): ReactNode {
  if (link === null) return <span>{none}</span>;
  if (/^https?:\/\//i.test(link)) {
    return (
      <a
        className="mj-ltr"
        dir="ltr"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {link}
      </a>
    );
  }
  return (
    <span className="mj-ltr" dir="ltr">
      {link}
    </span>
  );
}

export function EarlyAccessRequestsPage(): ReactNode {
  const { language } = useLanguage();
  const copy = COPY[language];
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const generation = useRef(0);

  const load = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;
    setState({ status: 'loading' });
    fetchEarlyAccessRequests().then(
      (page) => {
        if (generation.current !== mine) return;
        setState({
          status: 'ready',
          requests: page.requests,
          nextCursor: page.nextCursor,
          more: 'idle',
        });
      },
      (failure: unknown) => {
        if (generation.current !== mine) return;
        setState({
          status: 'failed',
          failure: failure instanceof ApiFailure ? failure : new ApiFailure('failed'),
        });
      },
    );
  }, []);

  useEffect(() => {
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const loadMore = useCallback(() => {
    if (
      state.status !== 'ready' ||
      state.nextCursor === null ||
      state.more === 'loading'
    ) {
      return;
    }
    const mine = generation.current;
    const after = state.nextCursor;
    setState({ ...state, more: 'loading' });
    fetchEarlyAccessRequests({ after }).then(
      (page) => {
        if (generation.current !== mine) return;
        setState((current) =>
          current.status === 'ready'
            ? {
                status: 'ready',
                requests: [...current.requests, ...page.requests],
                nextCursor: page.nextCursor,
                more: 'idle',
              }
            : current,
        );
      },
      () => {
        if (generation.current !== mine) return;
        setState((current) =>
          current.status === 'ready' ? { ...current, more: 'failed' } : current,
        );
      },
    );
  }, [state]);

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed')
    return <FailurePanel failure={state.failure} onRetry={load} />;

  return (
    <div>
      <p
        style={{
          font: 'var(--type-micro-cap)',
          letterSpacing: 'var(--type-micro-cap-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          margin: 0,
        }}
      >
        {copy.eyebrow}
      </p>
      <PageTitle>{copy.title}</PageTitle>
      <p style={{ maxWidth: '60ch', marginTop: 0 }}>{copy.intro}</p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-md)',
          marginBottom: 'var(--space-md)',
        }}
      >
        <span style={{ color: 'var(--text-muted)' }}>
          {copy.count(state.requests.length)}
        </span>
        <Button variant="secondary" size="sm" onClick={load}>
          {copy.refresh}
        </Button>
      </div>

      {state.requests.length === 0 ? (
        <EmptyState title={copy.empty} compact />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="mj-table">
            <thead>
              <tr>
                <th scope="col">{copy.name}</th>
                <th scope="col">{copy.email}</th>
                <th scope="col">{copy.brand}</th>
                <th scope="col">{copy.link}</th>
                <th scope="col">{copy.language}</th>
                <th scope="col">{copy.time}</th>
              </tr>
            </thead>
            <tbody>
              {state.requests.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    <span
                      className="mj-ltr"
                      dir="ltr"
                      style={{ font: 'var(--type-body-tabular)' }}
                    >
                      {row.email}
                    </span>
                  </td>
                  <td>{row.brand}</td>
                  <td>
                    <LinkCell link={row.link} none={copy.none} />
                  </td>
                  <td>{copy.languages[row.language]}</td>
                  <td>
                    <time dateTime={row.createdAt}>
                      {formatTime(row.createdAt, language)}
                    </time>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {state.more === 'failed' ? (
        <InlineAlert tone="warning" title={copy.moreFailed} role="alert">
          <Button variant="secondary" size="sm" onClick={loadMore}>
            {copy.retry}
          </Button>
        </InlineAlert>
      ) : null}
      {state.nextCursor !== null ? (
        <div style={{ marginTop: 'var(--space-md)' }}>
          <Button
            variant="secondary"
            onClick={loadMore}
            disabled={state.more === 'loading'}
            busy={state.more === 'loading'}
          >
            {state.more === 'loading' ? copy.loadingMore : copy.loadMore}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
